import { HARVEST_NOTES_MAX_LENGTH } from "@common/consts/harvestRecords";
import type { IWeightFormatter } from "@common/helpers/WeightFormatter";
import { ArrowLeftRight } from "lucide-react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "components/core/Accordion";
import { Button } from "components/core/Button";
import { Flex } from "components/core/Flex";
import { HarvestAmountField, SaveAsDefaultCheckbox } from "components/core/HarvestRecord";
import LucideIcon from "components/core/Icon/LucideIcon";
import { NumberInput } from "components/core/NumberInput";
import { Text } from "components/core/Text";
import { Textarea } from "components/core/Textarea";
import toggleHarvestUnits from "helpers/units/weight/toggleHarvestUnits";
import { usePersistedState } from "hooks";
import useHarvestRecordingSetting from "hooks/useHarvestRecordingSetting";

import DontAskAgainCheckbox from "./DontAskAgainCheckbox";
import { useCompletionFormContext } from "./useCompletionFormContext";

type Props = {
  formatter: IWeightFormatter;
  currencyPrefix: string;
  onWeightUnitChange: (unit: string) => void;
};

const COLLAPSE_KEY = "harvest";

const HarvestRecordSection = ({
  formatter,
  currencyPrefix,
  onWeightUnitChange
}: Props) => {
  const { watch, setValue, formState } = useCompletionFormContext();
  const harvestDisabled = watch("harvestDisabled");
  const harvestAmount = watch("harvestAmount");
  const harvestUnit = watch("harvestUnit");
  const harvestNotes = watch("harvestNotes");
  const harvestValuePerUnit = watch("harvestValuePerUnit");
  const harvestValueUnit = watch("harvestValueUnit");
  const saveAsDefault = watch("saveAsDefault");

  const [expanded, setExpanded] = usePersistedState<boolean>({
    key: "completion_modal_harvest_expanded",
    defaultValue: false
  });
  const { updateHarvestRecordingSetting, isUpdating } = useHarvestRecordingSetting();

  const handleDontAskAgain = (checked: boolean) => {
    setValue("harvestDisabled", checked);
    updateHarvestRecordingSetting(checked ? "off" : "on");
  };

  const toggleUnit = () => {
    const result = toggleHarvestUnits(
      formatter,
      harvestUnit,
      harvestAmount,
      harvestValuePerUnit
    );

    onWeightUnitChange(result.unit);
    setValue("harvestUnit", result.unit);
    setValue("harvestAmount", result.amount);
    setValue("harvestValueUnit", result.unit);
    setValue("harvestValuePerUnit", result.valuePerUnit);
  };

  const summary = harvestAmount ? `${harvestAmount} ${harvestUnit}` : "Skip";

  const valueLabel = (
    <Flex align="center" gap={1}>
      <Text variant="bodySmall" weight="semibold">
        Value
      </Text>
      <Button disabled={harvestDisabled} size="small" variant="text" onClick={toggleUnit}>
        ({currencyPrefix}/{harvestValueUnit})
        <LucideIcon color="currentColor" glyph={ArrowLeftRight} size="small" />
      </Button>
    </Flex>
  );

  const defaultHarvestValuePerUnit = formState.defaultValues?.harvestValuePerUnit;

  const collapseContent = (
    <Flex fullWidth direction="col" gap={2}>
      <Flex
        className={harvestDisabled ? "pointer-events-none opacity-40" : undefined}
        direction="col"
        gap={1}
      >
        <HarvestAmountField
          amount={harvestAmount}
          disabled={harvestDisabled}
          formatter={formatter}
          unit={harvestUnit}
          onAmountChange={(val) => setValue("harvestAmount", val)}
          onToggleUnit={toggleUnit}
        />
        {valueLabel}
        <NumberInput
          min={0.01}
          placeholder={`Value per ${harvestValueUnit} (optional)`}
          prefix={currencyPrefix}
          step={0.01}
          stepButtons={false}
          value={harvestValuePerUnit}
          onChange={(val) => setValue("harvestValuePerUnit", val)}
        />
        {harvestValuePerUnit != null &&
          defaultHarvestValuePerUnit !== harvestValuePerUnit && (
            <SaveAsDefaultCheckbox
              checked={saveAsDefault}
              onChange={(checked) => setValue("saveAsDefault", checked)}
            />
          )}
        <Text variant="bodySmall" weight="semibold">
          Notes
        </Text>
        <Textarea
          showCount
          maxLength={HARVEST_NOTES_MAX_LENGTH}
          placeholder="Add any notes about this harvest..."
          rows={2}
          value={harvestNotes}
          onChange={(e) => setValue("harvestNotes", e.target.value)}
        />
      </Flex>
      <DontAskAgainCheckbox
        checked={harvestDisabled}
        disabled={isUpdating}
        onCheckedChange={handleDontAskAgain}
      />
    </Flex>
  );

  return (
    <Accordion
      type="multiple"
      value={expanded ? [COLLAPSE_KEY] : []}
      onValueChange={(keys) => setExpanded(keys.includes(COLLAPSE_KEY))}
    >
      <AccordionItem value={COLLAPSE_KEY}>
        <AccordionTrigger
          extra={
            !expanded && (
              <Text color="subtle" variant="caption">
                {summary}
              </Text>
            )
          }
        >
          <Text color={harvestDisabled ? "subtle" : undefined} weight="semibold">
            Record Harvest
          </Text>
        </AccordionTrigger>
        <AccordionContent>{collapseContent}</AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export default HarvestRecordSection;
